import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/lib/i18n";
import { AutoBIMLogo } from "@/components/Brand";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button, Input, Card } from "@/components/ui";

const BG = "https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70";

export default function Login() {
  const { signIn, signInAsGuest, lang } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (!email || !password) return setErr(t("required", lang));
    setLoading(true);
    try {
      await signIn(email.trim().toLowerCase(), password);
      navigate("/studio");
    } catch (ex) {
      setErr(ex.message || t("error_generic", lang));
    } finally {
      setLoading(false);
    }
  }

  async function guest() {
    setLoading(true);
    try {
      await signInAsGuest();
      navigate("/studio");
    } catch (ex) {
      setErr(ex.message || "Guest sign-in failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen">
      <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-background/90 backdrop-blur-sm" />
      <div className="relative mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-10">
        <div className="mb-4 flex justify-end"><LanguageSwitcher /></div>
        <div className="mb-6 flex flex-col items-center gap-3">
          <AutoBIMLogo size={64} />
          <div className="text-3xl font-black">AutoBIM<span className="italic text-accent"> Logic</span></div>
          <p className="text-sm font-semibold italic text-accent">{isAr ? "أتمتة الـBIM بمنطق هندسي" : "Automate BIM · Engineer with Logic"}</p>
        </div>
        <Card>
          <h1 className="mb-4 text-xl font-black">{t("signIn", lang)}</h1>
          <form onSubmit={submit} className="space-y-3">
            <Input label={t("email", lang)} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input" />
            <Input label={t("password", lang)} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
            {err && <p className="text-sm text-destructive" data-testid="login-error">{err}</p>}
            <Button type="submit" className="w-full" loading={loading} data-testid="login-submit-button">{t("signInBtn", lang)}</Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            {t("noAccount", lang)}{" "}
            <Link to="/register" className="font-bold text-primary" data-testid="goto-register-link">{t("signUp", lang)}</Link>
          </p>
          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> {isAr ? "أو" : "or"} <div className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" className="w-full border-primary text-primary" onClick={guest} loading={loading} data-testid="guest-button">
            {isAr ? "متابعة كضيف (مشروع واحد، بدون تصدير)" : "Continue as guest (1 project, no exports)"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
